export type AppEnv = {
  VITE_KEYCLOAK_URL: string
  VITE_KEYCLOAK_CLIENT_ID: string
  VITE_API_BASE: string
}

declare global {
  interface Window {
    __BDI_CONFIG__?: { keycloakUrl?: unknown; keycloakClientId?: unknown }
  }
}

const envRecord = import.meta.env as Record<string, string | undefined>
const isTest = import.meta.env.MODE === 'test'
const isDevelopment = import.meta.env.MODE === 'development'
const runtime = window.__BDI_CONFIG__

// Only explicit test/development modes may use Vite values. Production must
// consume the startup-generated configuration even if old build args are set.
function keycloakValue(name: 'keycloakUrl' | 'keycloakClientId', viteName: keyof AppEnv, fallback: string): string {
  const value = runtime?.[name] ?? ((isTest || isDevelopment) ? envRecord[viteName] : undefined)
  if (typeof value === 'string' && value.length > 0) return value
  if (isTest) return fallback
  if (isDevelopment && envRecord.VITE_DISABLE_AUTH === 'true') return fallback
  throw new Error(`Missing or invalid runtime configuration: ${name}`)
}

const keycloakUrl = keycloakValue('keycloakUrl', 'VITE_KEYCLOAK_URL', 'https://example.com/realms/test')
const keycloakClientId = keycloakValue('keycloakClientId', 'VITE_KEYCLOAK_CLIENT_ID', 'test-client')
// The browser integration harness runs a loopback OIDC server in Vite dev mode.
const isLocalDevelopmentAuthority = isDevelopment && /^http:\/\/(?:localhost|127\.0\.0\.1)(?::[0-9]{1,5})?\/[A-Za-z0-9./_~-]+$/.test(keycloakUrl)
if (!isLocalDevelopmentAuthority && !/^https:\/\/[A-Za-z0-9.-]+(?::[0-9]{1,5})?\/realms\/[A-Za-z0-9._~-]+$/.test(keycloakUrl)) {
  throw new Error('Invalid runtime configuration: keycloakUrl must be an HTTPS realm URL')
}
if (keycloakClientId.length > 256 || /[\s\x00-\x1f\x7f]/.test(keycloakClientId)) { // eslint-disable-line no-control-regex
  throw new Error('Invalid runtime configuration: keycloakClientId')
}

export const env: AppEnv = {
  VITE_KEYCLOAK_URL: keycloakUrl,
  VITE_KEYCLOAK_CLIENT_ID: keycloakClientId,
  VITE_API_BASE: envRecord.VITE_API_BASE ?? (isTest ? 'http://localhost:8080/api/v1' : '/api/v1'),
}
